import { useRef } from 'react';

import { Box, Card, CardContent, Typography } from '@mui/material';

import DownArrowIcon from '@/assets/images/downRedCrackedArrow.svg';
import UpArrowIcon from '@/assets/images/upGreenCrackedArrow.svg';
import ElectricBorder from '@/features/Dashboard/components/ElectricBorder/ElectricBorder';
import { useScrollToAnimatedElement } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import type { TileType } from '@/pages/Markets/MarketsComponents/Tiles/types';
import { getDiffColor } from '@/utils/getDiffColor';

const cardContentSx = {
  width: '100%',
  p: '6px 8px 6px 4px',
  '&:last-child': { pb: '2px' }
};
const topRowSx = { display: 'flex', justifyContent: 'space-between' };
const codeValueSx = { fontSize: '24px', fontWeight: 600, lineHeight: '1' };
const bottomRowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const shortTitleSx = { fontSize: '14px', mr: 1, position: 'relative', pr: '18px' };
const factsheetBtnWrapSx = {
  position: 'absolute',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)'
};
const deltaRowSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  justifyContent: 'flex-end'
};
const arrowSx = { fontSize: 12, marginRight: 1 };

const Tile = ({ tile }: { tile: TileType }) => {
  const { code, shortTitle, value, delta, deltaPercentage } = tile;
  const ref = useRef<HTMLDivElement>(null);
  const isAnimating = useScrollToAnimatedElement(ref, tile.id);
  const priceColor = getDiffColor(Number(delta));

  return (
    <ElectricBorder animate={isAnimating}>
      <Card
        ref={ref}
        sx={{
          display: 'flex',
          flexGrow: 1,
          borderLeft: `4px solid`,
          borderColor: priceColor,
          lineHeight: 1
        }}
      >
        <CardContent sx={cardContentSx}>
          <Box sx={topRowSx}>
            <Typography
              noWrap
              component='div'
              sx={codeValueSx}
            >
              {code || 'GXCODE'}
            </Typography>
            <Typography
              component='div'
              sx={codeValueSx}
            >
              {value}
            </Typography>
          </Box>
          <Box sx={bottomRowSx}>
            <Typography
              noWrap
              component='div'
              sx={shortTitleSx}
            >
              {shortTitle}
              <Box sx={factsheetBtnWrapSx}>
                <FactsheetDialogButton
                  showTooltip
                  code={code}
                  fontSize={14}
                />
              </Box>
            </Typography>

            <Box sx={deltaRowSx}>
              {!!delta && Number(delta) !== 0 && (
                <Box
                  alt={String(delta).startsWith('-') ? 'Down' : 'Up'}
                  component='img'
                  src={String(delta).startsWith('-') ? DownArrowIcon : UpArrowIcon}
                  sx={arrowSx}
                />
              )}
              <Typography>{delta}&nbsp;</Typography>
              <Typography>({deltaPercentage}%)</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </ElectricBorder>
  );
};

export default Tile;
